import { BACKEND_URL } from "@/lib/api";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${BACKEND_URL}/api`;

/**
 * Order an AI System modal — direct checkout, no need to sign in.
 *
 * Payment options: Stripe Checkout (card) OR Bank transfer (PDF invoice emailed).
 * Total = setup + first month, prepaid upfront. 20% VAT calculated at checkout.
 */
export default function OrderAiSystemModal({ open, onOpenChange, system }) {
  const [form, setForm] = useState({ company: "", name: "", email: "" });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  if (!system) return null;

  const subtotal = (system.setup || 0) + (system.monthly || 0);
  const vat = +(subtotal * 0.2).toFixed(2);
  const total = +(subtotal + vat).toFixed(2);

  const validate = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Required";
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ai-orders/checkout`, {
        slug: system.slug,
        company: form.company.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      });
      if (data.payment_method === "bank_transfer") {
        toast.success(`Invoice ${data.invoice_number} emailed — pay within 14 days to activate.`);
        setTimeout(() => onOpenChange(false), 2500);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ai-order-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Order {system.name}</DialogTitle>
          <DialogDescription>
            Prepaid · setup + first month upfront · 20% VAT applied at checkout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-1.5 text-sm">
            <Row k={`${system.name} — setup`} v={`£${(system.setup || 0).toLocaleString()}`} />
            <Row k={`${system.name} — first month`} v={`£${(system.monthly || 0).toLocaleString()}`} />
            <div className="border-t border-slate-200 pt-1.5">
              <Row k="Subtotal (ex-VAT)" v={`£${subtotal.toLocaleString()}`} />
              <Row k="VAT @ 20%" v={`£${vat.toLocaleString()}`} />
            </div>
            <div className="border-t border-slate-200 pt-1.5 flex items-center justify-between font-medium">
              <span className="text-slate-700">Total due now</span>
              <span className="text-slate-900 tabular-nums text-base">£{total.toLocaleString()}</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Ltd" data-testid="aio-company" />
              {errors.company && <p className="text-[11px] text-rose-600 mt-1">{errors.company}</p>}
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Your name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="aio-name" />
              {errors.name && <p className="text-[11px] text-rose-600 mt-1">{errors.name}</p>}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Work email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@acme.com" data-testid="aio-email" />
            {errors.email && <p className="text-[11px] text-rose-600 mt-1">{errors.email}</p>}
            <p className="text-[11px] text-slate-500 mt-1">We'll email the invoice + setup instructions here.</p>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600 mb-2 block">Payment method</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              <PMButton active={paymentMethod === "card"} onClick={() => setPaymentMethod("card")} Icon={CreditCard} title="Pay with card" subtitle="Stripe Checkout · instant activation" testid="aio-pm-card" />
              <PMButton active={paymentMethod === "bank_transfer"} onClick={() => setPaymentMethod("bank_transfer")} Icon={Banknote} title="Bank transfer" subtitle="PDF invoice · 14-day terms" testid="aio-pm-bank" />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="aio-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="aio-submit">
            {busy ? <><Loader2 className="size-4 animate-spin" /> Processing…</> : <>Order · £{total.toLocaleString()} <ArrowRight className="size-4" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 text-[12px]">{k}</span>
      <span className="text-slate-900 text-[12.5px] font-medium tabular-nums">{v}</span>
    </div>
  );
}

function PMButton({ active, onClick, Icon, title, subtitle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-3.5 py-3 transition-all ${active ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/20" : "border-slate-200 hover:border-slate-300"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-grid place-items-center size-7 rounded-md ${active ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-700"}`}>
          <Icon className="size-3.5" strokeWidth={1.7} />
        </span>
        <span className="font-medium text-slate-900 text-[13px]">{title}</span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5 pl-9">{subtitle}</p>
    </button>
  );
}
